<template>
  <div class="custom-layout" :class="{ 'is-collapsed': sidebarCollapsed }">
    <!-- 左侧导航栏 -->
    <aside class="custom-sidebar">
      <!-- 折叠切换按钮 -->
      <button class="sidebar-collapse-btn" :title="sidebarCollapsed ? '展开菜单' : '收起菜单'" @click="sidebarCollapsed = !sidebarCollapsed">
        <el-icon v-if="sidebarCollapsed"><Expand /></el-icon>
        <el-icon v-else><Fold /></el-icon>
      </button>

      <!-- Logo -->
      <router-link to="/" class="sidebar-logo">
        <el-image
          class="sidebar-logo-img"
          src="https://pic.xuefou.com/pic/20260826/1800/0357ce0740b24d94b59a76ff88e63226.png"
          fit="contain"
        />
      </router-link>

      <!-- 中间可滚动区域：AI 创作卡片 + 常规菜单 -->
      <div class="sidebar-scroll">
        <!-- AI 创作卡片 -->
        <div v-if="aiMenu && checkResource(aiMenu.code as string)" v-show="!sidebarCollapsed" class="ai-panel">
          <div class="ai-panel-header">
            <div class="ai-panel-title-row">
              <span class="ai-panel-title">AI 创作</span>
              <span class="ai-panel-badge">{{ aiSceneTemplates.length }}个场景</span>
            </div>
            <div class="ai-panel-decoration">
              <div class="ai-panel-deco-card" />
              <span class="ai-panel-deco-text">AI</span>
              <span class="ai-panel-deco-star ai-panel-deco-star--top">✦</span>
              <span class="ai-panel-deco-star ai-panel-deco-star--bottom">✦</span>
            </div>
          </div>
          <div class="ai-panel-items">
            <router-link
              v-for="item in aiSceneTemplates"
              :key="item.id"
              :to="'/l/merchant/aiCreate?id=' + item.id"
              class="ai-panel-item"
              :class="{ 'ai-panel-item--active': route.path === '/l/merchant/aiCreate' && route.query.id == String(item.id) }"
            >
              <div style="flex-shrink: 0;"><LyIcon size="15" ic="MagicStick" /></div>
              <span>{{ item.sceneName }}</span>
            </router-link>
          </div>
        </div>

        <!-- 菜单区域 -->
        <div class="sidebar-menus">
          <template v-for="(menu, menuIdx) in regularMenus" :key="menuIdx">
            <!-- 有子菜单的分组 -->
            <div
              v-if="menu.children && menu.children.length && checkResource(menu.code as string)"
              class="menu-group"
              :class="{ 'menu-group--open': openGroups[menuIdx] }"
            >
              <div class="menu-group-header" @click="toggleGroup(menuIdx)">
                <div class="menu-group-icon">
                  <LyIcon size="18" :ic="menu.icon!" />
                </div>
                <div class="menu-group-info">
                  <div class="menu-group-title">{{ menu.name }}</div>
                  <div class="menu-group-subtitle">{{ (menu as any).subtitle || '' }}</div>
                </div>
                <svg
                  class="menu-group-arrow"
                  :class="{ 'menu-group-arrow--collapsed': !openGroups[menuIdx] }"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
              <div v-show="openGroups[menuIdx]" class="menu-group-items">
                <router-link
                  v-for="child in menu.children"
                  :key="child.code"
                  :to="child.link || ''"
                  class="menu-item-link"
                  :class="{ 'menu-item-link--active': isMenuActive(child) }"
                >
                  <div class="menu-item-indicator" v-if="isMenuActive(child)" />
                  <LyIcon class="menu-item-icon" size="18" :ic="child.icon || menu.icon!" />
                  <span class="menu-item-name">{{ child.name }}</span>
                </router-link>
              </div>
            </div>

            <!-- 无子菜单的单项 -->
            <router-link
              v-else-if="checkResource(menu.code as string)"
              :to="menu.link || ''"
              class="menu-item-link menu-item-link--solo"
              :class="{ 'menu-item-link--active': isMenuActive(menu) }"
            >
              <div class="menu-item-indicator" v-if="isMenuActive(menu)" />
              <LyIcon class="menu-item-icon" size="18" :ic="menu.icon!" />
              <span class="menu-item-name">{{ menu.name }}</span>
            </router-link>
          </template>
        </div>
      </div>

      <!-- AI 豆卡片 -->
      <div class="sidebar-balance" v-show="!sidebarCollapsed">
        <div class="balance-header">
          <span class="balance-title">AI豆余额</span>
          <span class="balance-detail" @click="goAiBeanDetail">明细 &gt;</span>
        </div>
        <div class="balance-amount">
          <span class="balance-num">{{ aiBeanAmount }}</span>
          <img class="balance-emoji" src="https://pic.xuefou.com/pic/20260814/1800/776887283d554f80925e3fc77d95610e.png" alt="AI豆" />
        </div>
        <el-button class="balance-btn" type="primary" round @click="openRecharge">
          立即充值
        </el-button>
      </div>

      <!-- 充值弹窗 -->
      <RechargeDialog ref="rechargeRef" @success="loadBalance" />
    </aside>

    <!-- 右侧主区域 -->
    <div class="custom-main">
      <!-- 顶部导航栏 -->
      <header class="custom-topnav">
        <el-dropdown trigger="click">
          <div class="topnav-user">
            <div class="topnav-avatar">
              <el-avatar :size="32" :src="shareState.currentUser?.head || ''">
                <template v-if="shareState.currentUser?.name">
                  {{ shareState.currentUser.name.substring(0, 1) }}
                </template>
                <template v-else>
                  <el-icon :size="18"><UserFilled /></el-icon>
                </template>
              </el-avatar>
            </div>
            <span class="topnav-name">{{ shareState.currentUser?.name || '管理员' }}</span>
            <svg
              class="topnav-arrow"
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item @click="handleLogout">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>退出登录</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </header>

      <!-- 内容区域 -->
      <div class="custom-content">
        <LyModulePage :key="route.fullPath" />
        <LyFooter :company="shareState.company" :year="shareState.copyrightYear"></LyFooter>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { UserFilled, Fold, Expand } from '@element-plus/icons-vue'
import {
  LyModulePage,
  LyFooter,
  LyIcon,
  shareState,
  checkResource,
  http,
  type DhForMenus
} from '@dh/dh-for-vue'
import RechargeDialog from '@/modules/merchant/views/ai-bean/recharge-dialog.vue'

const route = useRoute()
const router = useRouter()

// 侧边栏折叠状态
const sidebarCollapsed = ref(false)

// AI 豆余额
const aiBeanAmount = ref<number>(0)
const rechargeRef = ref<InstanceType<typeof RechargeDialog>>()

async function loadBalance() {
  try {
    const res: any = await http.get('/institutionAdmin/aiBeanBalance/mine')
    aiBeanAmount.value = res.data?.data?.beanAmount ?? 0
  } catch (e) {
    aiBeanAmount.value = 0
  }
}

function goAiBeanDetail() {
  router.push('/l/merchant/aiBeanDetail')
}

async function openRecharge() {
  await router.push('/l/merchant/aiBeanDetail')
  nextTick(() => {
    rechargeRef.value?.open()
  })
}

// 从 shareState 获取菜单
const menus = ref<DhForMenus[]>([])
const updateMenus = () => {
  if (shareState.apps.length > 0) {
    const allMenus: DhForMenus[] = []
    shareState.apps.forEach(app => {
      app.menus.forEach(m => allMenus.push(m))
    })
    menus.value = allMenus
  }
}
watch(() => shareState.apps, updateMenus, { immediate: true, deep: true })

// AI 创作菜单（独立渐变卡片）
const aiMenu = computed(() => {
  return menus.value.find(m => m.icon === 'MagicStick') || null
})

// AI 场景模板（动态从接口获取）
const aiSceneTemplates = ref<{ id: number; sceneName: string }[]>([])

onMounted(async () => {
  loadBalance()
  try {
    const res: any = await http.get('/institutionAdmin/aiSceneTemplate/enabled')
    if (res.data?.success) {
      aiSceneTemplates.value = res.data.data || []
    }
  } catch (e) {
    console.error('加载AI场景模板失败', e)
  }
})

// 常规菜单（排除 AI 创作）
const regularMenus = computed(() => {
  return menus.value.filter(m => m.icon !== 'MagicStick')
})

// 分组展开状态
const openGroups = reactive<Record<number, boolean>>({})
watch(regularMenus, (newMenus) => {
  newMenus.forEach((_, idx) => {
    if (openGroups[idx] === undefined) {
      openGroups[idx] = true
    }
  })
}, { immediate: true })

const toggleGroup = (idx: number) => {
  openGroups[idx] = !openGroups[idx]
}

// 菜单激活判断
const isMenuActive = (menu: DhForMenus) => {
  if (menu.link) {
    return route.path === menu.link
  }
  if (menu.children) {
    return menu.children.some(child => route.path === child.link)
  }
  return false
}

// 退出登录
const handleLogout = () => {
  document.cookie = "auth-token=;expires=" + new Date().toUTCString()
  document.cookie = "auth-role=;expires=" + new Date().toUTCString()
  window.location.href = '/merchant/login.html'
}
</script>

<style lang="scss" scoped>
.custom-layout {
  --ly-slide-width: 232px;
  display: flex;
  min-height: 100vh;
  background: #fbfcff;
  /* 必须用 clip 而非 hidden：hidden 会让 overflow-y 计算为 auto，
     使 .custom-layout 变成滚动容器，导致页面内 .footer-bar 的
     position: sticky; bottom: 0 锚定在错误的滚动容器上而失效 */
  overflow-x: clip;
}

/* ===== 左侧导航栏 ===== */
.custom-sidebar {
  width: 232px;
  min-width: 232px;
  height: 100vh;
  background: #fff;
  box-shadow: 12px 0 50px rgba(30, 42, 75, 0.04);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1001;
  transition: width 0.25s ease;
}

/* 中间可滚动区域：AI 创作卡片 + 常规菜单 */
.sidebar-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;
}

/* ===== 折叠切换按钮 ===== */
.sidebar-collapse-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: -12px;
  z-index: 1003;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid #e6e0ff;
  border-radius: 50%;
  background: #fff;
  color: #6d55d8;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(102, 71, 255, 0.12);
  transition: all 0.2s;

  &:hover {
    background: #f5f3ff;
    color: #5a40c8;
  }
}

/* ===== 折叠态 ===== */
.custom-layout.is-collapsed {
  --ly-slide-width: 64px;

  .custom-sidebar {
    width: 64px;
    min-width: 64px;
  }

  .sidebar-logo {
    justify-content: center;
    padding: 0 8px;
  }

  .sidebar-scroll {
    padding: 0 8px;
  }

  .sidebar-menus {
    padding: 0;
    gap: 8px;
    align-items: center;
  }

  /* 折叠态隐藏分组标题/副标题/箭头，仅保留图标 */
  .menu-group {
    background: transparent;
  }

  .menu-group-header {
    justify-content: center;
    padding: 0;
    height: 44px;
    border-radius: 10px;

    &:hover {
      background: #f6f3ff;
    }
  }

  .menu-group-info,
  .menu-group-arrow {
    display: none;
  }

  .menu-group-items {
    display: none;
  }

  .menu-item-link,
  .menu-item-link--solo {
    justify-content: center;
    padding: 0;
    height: 40px;
    gap: 0;
  }

  .menu-item-name {
    display: none;
  }

  .menu-item-indicator {
    display: none;
  }

  .menu-item-link--active {
    background: #f6f3ff;
    box-shadow: none;
  }

  /* 右侧主区域同步收窄 */
  .custom-main {
    margin-left: 64px;
  }

  .custom-topnav {
    left: 64px;
  }
}

/* Logo */
.sidebar-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 72px;
  padding: 0 16px;
  text-decoration: none;
  flex-shrink: 0;
}

.sidebar-logo-img {
  width: 100%;
  height: 100%;
  overflow: hidden;

  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

/* 菜单区域：高度随菜单数量动态，由外层 .sidebar-scroll 统一滚动 */
.sidebar-menus {
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 菜单分组 */
.menu-group {
  border-radius: 12px;
  overflow: hidden;
  background: #f6f3ff;
  transition: background 0.2s;
}

.menu-group--open {
  background: #f6f3ff;
}

/* ===== AI 创作渐变卡片 ===== */
.ai-panel {
  margin: 0 16px 12px;
  padding: 12px;
  border-radius: 12px;
  height: 250px;
  flex: 0 0 250px;
  background: linear-gradient(135deg, #7a54ff, #b83bfa, #ed67cb);
  color: #fff;
  box-shadow: 0 22px 48px rgba(125, 67, 246, 0.20);
  display: flex;
  flex-direction: column;
}

.ai-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.ai-panel-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ai-panel-title {
  font-size: 15px;
  font-weight: 400;
  letter-spacing: 0.04em;
}

.ai-panel-badge {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.18);
  border-radius: 100px;
  padding: 2px 8px;
}

/* AI 装饰徽章 */
.ai-panel-decoration {
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  opacity: 0.75;
}

.ai-panel-deco-card {
  position: absolute;
  right: 4px;
  top: 12px;
  width: 24px;
  height: 24px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  transform: rotate(12deg);
}

.ai-panel-deco-text {
  position: absolute;
  right: 12px;
  top: 20px;
  font-size: 10px;
  font-weight: 700;
}

.ai-panel-deco-star {
  position: absolute;

  &--top {
    right: 0;
    top: 0;
    font-size: 16px;
  }

  &--bottom {
    left: 0;
    top: 20px;
    font-size: 14px;
  }
}

.ai-panel-items {
  /* flex 自适应：填满卡片剩余高度（卡片固定250px），超出时内部滚动 */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.45) transparent;
}

.ai-panel-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.95);
  text-decoration: none;
  transition: background 0.2s;
  margin-bottom: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
}

.ai-panel-item--active {
  background: rgba(255, 255, 255, 0.2);
}

.menu-group-header {
  display: flex;
  align-items: center;
  height: 54px;
  padding: 0 16px;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}

.menu-group-icon {
  width: 32px;
  height: 32px;
  border-radius: 12px;
  background: #7651dc;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 10px 22px rgba(118, 81, 220, 0.24);
}

.menu-group-info {
  flex: 1;
  min-width: 0;
}

.menu-group-title {
  font-size: 14px;
  font-weight: 400;
  color: #6250c8;
  line-height: 20px;
}

.menu-group-subtitle {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 400;
  color: #9a95ad;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.menu-group-arrow {
  color: #6d55d8;
  flex-shrink: 0;
  transition: transform 0.25s ease;
}

.menu-group-arrow--collapsed {
  transform: rotate(-90deg);
}

/* 菜单项列表 */
.menu-group-items {
  padding: 4px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 菜单项链接 */
.menu-item-link {
  position: relative;
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  gap: 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 400;
  color: #252b3e;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.6);
  }
}

.menu-item-link--active {
  background: #fff;
  color: #6d3de8;
  box-shadow: 0 8px 18px rgba(118, 81, 220, 0.1);

  &:hover {
    background: #fff;
  }

  .menu-item-icon {
    color: #7651dc;
    margin-left: 4px;
  }
}

.menu-item-link--solo {
  margin-top: 4px;
}

.menu-item-indicator {
  position: absolute;
  left: 0;
  top: 8px;
  width: 4px;
  height: 20px;
  border-radius: 0 4px 4px 0;
  background: #7651dc;
}

.menu-item-icon {
  color: #7651dc;
  flex-shrink: 0;
}

.menu-item-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ===== AI 豆卡片（固定底部） ===== */
.sidebar-balance {
  margin: 0 16px 16px;
  padding: 16px 14px;
  border-radius: 14px;
  border: 1px solid #e6e0ff;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(102, 71, 255, 0.06);
  flex-shrink: 0;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.balance-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.balance-title {
  font-size: 13px;
  color: #6d55d8;
}

.balance-detail {
  font-size: 13px;
  color: #6d55d8;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s;
}

.balance-detail:hover {
  opacity: 0.7;
}

.balance-amount {
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}

.balance-num {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  color: #1b243d;
}

.balance-emoji {
  width: 22px;
  height: 22px;
  object-fit: contain;
  vertical-align: middle;
}

.balance-avatar {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #6d55d8;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(109, 85, 216, 0.25);
}

.balance-btn {
  width: 100%;
  height: 36px;
  font-size: 14px;
  background: #6d55d8;
  border-color: #6d55d8;
}

.balance-btn:hover {
  background: #5a40c8;
  border-color: #5a40c8;
}

/* ===== 右侧主区域 ===== */
.custom-main {
  flex: 1;
  margin-left: 232px;
  padding-top: 56px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 0;
}

/* 顶部导航栏 */
.custom-topnav {
  position: fixed;
  top: 0;
  left: 232px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 56px;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #edf0f6;
  z-index: 1002;
  transition: left 0.25s ease;
}

.topnav-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 8px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f7f4ff;
  }
}

.topnav-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #ffcf3f, #ff7ab6, #6c7cff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 10px 24px rgba(255, 122, 182, 0.28);

  :deep(.el-avatar) {
    background: transparent;
  }
}

.topnav-name {
  font-size: 14px;
  font-weight: 400;
  color: #1b243d;
}

.topnav-arrow {
  color: #8a94aa;
  transition: transform 0.25s;
}

/* 内容区域 */
.custom-content {
  flex: 1;
  min-height: 0;
}
</style>
